import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  ClipboardList, Filter, User, DollarSign, CheckCircle, XCircle, 
  Edit, Trash2, TrendingUp, TrendingDown, Settings, Loader2, Calendar
} from 'lucide-react';

export default function AdminAuditLog() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, action_counts: {} });
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = {};
      if (actionFilter !== 'all') params.action_type = actionFilter;
      params.limit = 200;
      
      const response = await adminAPI.getAuditLogs(params);
      setLogs(response.data.logs || []);
      setStats(response.data.stats || { total: 0, action_counts: {} });
    } catch (error) {
      toast.error('Failed to fetch audit logs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [actionFilter]);

  const getActionIcon = (actionType) => {
    const icons = {
      user_edited: Edit,
      user_updated: Edit,
      user_suspended: User,
      user_banned: XCircle,
      user_deleted: Trash2,
      balance_adjusted: DollarSign,
      profit_added: TrendingUp,
      loss_added: TrendingDown,
      deposit_approved: CheckCircle,
      deposit_rejected: XCircle,
      withdrawal_approved: CheckCircle,
      withdrawal_rejected: XCircle,
      settings_updated: Settings
    };
    return icons[actionType] || ClipboardList;
  };

  const getActionColor = (actionType) => {
    const colors = {
      user_edited: 'text-blue-400 bg-blue-500/20',
      user_updated: 'text-blue-400 bg-blue-500/20',
      user_suspended: 'text-orange-400 bg-orange-500/20',
      user_banned: 'text-red-400 bg-red-500/20',
      user_deleted: 'text-red-400 bg-red-500/20',
      balance_adjusted: 'text-cyan-400 bg-cyan-500/20',
      profit_added: 'text-emerald-400 bg-emerald-500/20',
      loss_added: 'text-red-400 bg-red-500/20',
      deposit_approved: 'text-emerald-400 bg-emerald-500/20',
      deposit_rejected: 'text-red-400 bg-red-500/20',
      withdrawal_approved: 'text-emerald-400 bg-emerald-500/20',
      withdrawal_rejected: 'text-red-400 bg-red-500/20',
      settings_updated: 'text-purple-400 bg-purple-500/20'
    };
    return colors[actionType] || 'text-gray-400 bg-gray-500/20';
  };

  const formatActionType = (actionType) => {
    return actionType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown Action';
  };

  const actionTypes = [
    { value: 'all', label: 'All Actions' },
    { value: 'user_edited', label: 'User Edited' },
    { value: 'user_suspended', label: 'User Suspended' },
    { value: 'user_banned', label: 'User Banned' },
    { value: 'user_deleted', label: 'User Deleted' },
    { value: 'balance_adjusted', label: 'Balance Adjusted' },
    { value: 'profit_added', label: 'Profit Added' },
    { value: 'loss_added', label: 'Loss Added' },
    { value: 'deposit_approved', label: 'Deposit Approved' },
    { value: 'deposit_rejected', label: 'Deposit Rejected' },
    { value: 'withdrawal_approved', label: 'Withdrawal Approved' },
    { value: 'withdrawal_rejected', label: 'Withdrawal Rejected' },
    { value: 'settings_updated', label: 'Settings Updated' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Audit Log</h1>
          <p className="text-gray-400 text-sm mt-1">Track all administrative actions on the platform</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-white">{stats.total}</p>
          <p className="text-sm text-gray-400">Total Actions</p>
        </div>
      </div>

      {/* Action Type Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {Object.entries(stats.action_counts || {}).slice(0, 6).map(([action, count], index) => {
          const Icon = getActionIcon(action);
          const colorClass = getActionColor(action);
          return (
            <motion.div
              key={action}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-3"
            >
              <div className={`w-8 h-8 rounded-lg ${colorClass} flex items-center justify-center mb-2`}>
                <Icon className="w-4 h-4" />
              </div>
              <p className="text-lg font-bold text-white">{count}</p>
              <p className="text-xs text-gray-400 truncate">{formatActionType(action)}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-xs">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="w-full pl-10 pr-8 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500 transition-colors"
            data-testid="action-filter-select"
          >
            {actionTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Logs Table - Desktop */}
      <div className="hidden lg:block bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Action</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Target User</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Note</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Date & Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mx-auto" />
                    <p className="text-gray-400 mt-2">Loading audit logs...</p>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <ClipboardList className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No audit logs found</p>
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  const Icon = getActionIcon(log.action_type);
                  const colorClass = getActionColor(log.action_type);
                  return (
                    <tr key={log.id} className="hover:bg-gray-700/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg ${colorClass} flex items-center justify-center`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <span className="text-white font-medium">{formatActionType(log.action_type)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-300">{log.admin_username || 'System'}</td>
                      <td className="px-6 py-4">
                        {log.target_user_email ? (
                          <span className="text-cyan-400">{log.target_user_email}</span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {log.amount != null ? (
                          <span className={log.amount >= 0 ? 'text-emerald-400 font-semibold' : 'text-red-400 font-semibold'}>
                            {log.amount >= 0 ? '+' : ''}{log.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-400 text-sm max-w-xs truncate">
                        {log.note || '-'}
                      </td>
                      <td className="px-6 py-4 text-gray-400 text-sm">
                        {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Logs Cards - Mobile/Tablet */}
      <div className="lg:hidden space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mx-auto" />
            <p className="text-gray-400 mt-2">Loading audit logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardList className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No audit logs found</p>
          </div>
        ) : (
          logs.map((log, index) => {
            const Icon = getActionIcon(log.action_type);
            const colorClass = getActionColor(log.action_type);
            return (
              <motion.div
                key={log.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg ${colorClass} flex items-center justify-center shrink-0`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium">{formatActionType(log.action_type)}</p>
                    <p className="text-gray-400 text-sm">by {log.admin_username || 'System'}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-gray-400 text-xs">
                      <Calendar className="w-3 h-3" />
                      {log.created_at ? new Date(log.created_at).toLocaleDateString() : '-'}
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs">Target User</p>
                    <p className="text-cyan-400 truncate">{log.target_user_email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Amount</p>
                    {log.amount != null ? (
                      <p className={log.amount >= 0 ? 'text-emerald-400 font-semibold' : 'text-red-400 font-semibold'}>
                        {log.amount >= 0 ? '+' : ''}${Math.abs(log.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </p>
                    ) : (
                      <p className="text-gray-500">-</p>
                    )}
                  </div>
                </div>
                
                {log.note && (
                  <div className="mt-3 pt-3 border-t border-gray-700/50">
                    <p className="text-gray-400 text-sm">{log.note}</p>
                  </div>
                )}
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
}
